import type { components } from '@/types/api/v1'
import { NotificationMessage } from '@gravitate-js/excalibrr'
import { Modal } from 'antd'
import axios from 'axios'
import _ from 'lodash'
import moment from 'moment'

import { getFilterWithCalendarShiftDate } from '@/api/useOrders/util'
import { getCurrentMarkets, getTimezone, setCurrentMarkets } from './general'
import { getLocaleSetting, setLocaleSetting } from './intl/language'

const store = window.localStorage

export const getAPIURL = (isLoader?: boolean) => {
  const url = import.meta.env.VITE_API_URL || window.location.href
  // starting on the 8th character after "http://" or "https://", find '/'
  const slash_loc = url.indexOf('/', 8)
  let cutURL = url.substr(0, slash_loc + 1)
  const hasTrailingSlash = cutURL.lastIndexOf('/') === cutURL.length - 1
  if (!hasTrailingSlash) {
    cutURL += '/'
  }
  if (isLoader) {
    return `${cutURL}service/loader/`
  }
  if (import.meta.env.VITE_ARE_YOU_NGROCKIN) {
    return cutURL
  }
  return `${cutURL}api/`
}

export const getBlob = (endpoint, query_params?, isLoaderEP?) => {
  const token = store?.getItem('token')
  return axios.get(`${getAPIURL(isLoaderEP)}${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
    params: query_params,
    responseType: 'blob',
    timeout: 200000,
  })
}

export const postBlob = (endpoint, body, isLoaderEP?) => {
  const token = store?.getItem('token')
  return axios.post(`${getAPIURL(isLoaderEP)}${endpoint}`, body, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob',
    timeout: 200000,
  })
}

const post = (endpoint, data, query_params, cancelToken) => {
  if (data instanceof Object || query_params instanceof Object) {
    const currentMarkets = getCurrentMarkets()
    if (data && !_.has(data, 'markets')) data.markets = currentMarkets
    if (query_params && !_.has(query_params, 'markets')) query_params.markets = currentMarkets
    if (!data && !query_params) query_params = { markets: currentMarkets }
  }
  return axios.post(`${getAPIURL()}${endpoint}`, data, {
    params: query_params,
    cancelToken: cancelToken && cancelToken.token,
    'Accept-Language': getLocaleSetting(),
  })
}
let refreshInFlight: Promise<string | null> | null = null

const requestAccessToken = async (): Promise<string | null> => {
  if (!store.getItem('refresh')) return null
  // The request interceptor falls back to `Refresh <refresh>` only when no token is stored.
  store.removeItem('token')
  try {
    const res = await post('token', undefined, undefined, undefined)
    if (res?.status === 200) {
      store.setItem('token', res.data.access_token)
      store.setItem('accessToken', res.data.access_token)
      return res.data.access_token
    }
  } catch (e) {
    if (!(axios.isAxiosError(e) && e.response?.status === 401)) return null
  }
  store.removeItem('refresh')
  store.removeItem('refreshToken')
  return null
}

export function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = requestAccessToken().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

const blob = (endpoint, data, isLoaderEP, params) => {
  const token = store?.getItem('token')
  return axios.post(`${getAPIURL(isLoaderEP)}${endpoint}`, data, {
    headers: { Authorization: `Bearer ${token}`, accept: 'application/json' },
    responseType: 'blob',
    timeout: 30000,
    params,
  })
}

const uploadFile = async (endpoint, data, params, isLoaderEP) => {
  const token = store?.getItem('token')
  return axios.post(`${getAPIURL(isLoaderEP)}${endpoint}`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    },
    params,
    timeout: 600000,
  })
}

const handleErrors = async (resp) => {
  if (!resp || resp?.status === 500) {
    NotificationMessage('Oops', 'Something unexpected happened. If this issue continues, please contact support.')
    return
  }

  if (resp?.data instanceof Blob) {
    const text = await resp?.data.text()
    const json = await JSON.parse(text)
    if (json.detail && typeof json.detail === 'string') {
      NotificationMessage('Oops!', json.detail)
      return false
    }
  }

  if (resp.data && resp.data.detail) {
    if (typeof resp.data.detail === 'string') {
      NotificationMessage('Oops!', resp.data.detail)
      return false
    }

    resp.data?.detail.forEach((d) => {
      if (d.loc && d.loc.length > 0) {
        d.loc.forEach((l, i) => {
          // first value will be "body", so we can skip that
          if (i === 0) return
          let property = l.replace('_', ' ')
          property = property.charAt(0) + property.slice(1)
          NotificationMessage('Oops!', `${property} - ${d.msg}`)
        })
      }
    })
  }
}
export const clearStickyPages = () => {
  const theme = store.getItem('themeStorage')
  const locale = getLocaleSetting()
  const featureFlags = Object.entries(store).filter(([key]) => key.startsWith('featureFlags:'))
  store.clear()
  if (theme) store.setItem('themeStorage', theme)
  for (const [key, value] of featureFlags) store.setItem(key, value)
  setLocaleSetting(locale ?? 'en', true)
}
axios.interceptors.request.use(
  (config) => {
    const token = store.getItem('token')
    const refresh = store.getItem('refresh')

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    } else if (refresh) {
      config.headers.Authorization = `Refresh ${refresh}`
    }
    config.headers.accept = 'application/json'
    config.headers['Accept-Language'] = getLocaleSetting() ?? 'en'
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

axios.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    const originalRequest = error ? error.config : null
    if (error.response.status === 422) {
      handleErrors(error.response)
      return Promise.reject(error)
    }
    if (error.response.status === 500) {
      NotificationMessage('Oops!', 'Something unexpected happened. If this issue continues, please contact support.')
      return Promise.reject(error)
    }
    if (error.response.data.detail === 'Not enough scopes for this endpoint') {
      NotificationMessage(
        'You are not allowed access. ',
        'if you feel this is a mistake, please contact your system administrator'
      )
      return
    }
    if (error.response.status === 401 && originalRequest.url.includes('/token')) {
      store.removeItem('token')
      store.removeItem('refresh')
    } else if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      return refreshAccessToken().then((token) => {
        if (token) {
          axios.defaults.headers.common.Authorization = `Bearer ${token}`
          return axios(originalRequest)
        }
        return Promise.reject(error)
      })
    }

    if (!window.location.href.includes('login')) {
      NotificationMessage('Session Expired', 'Please login again to continue.')
    }
    return Promise.reject(error)
  }
)

const q = {
  setHandleLogout: (handleLogout) => {
    axios.interceptors.response.use(
      (response) => {
        return response
      },
      (error) => {
        if (!error) return
        if (error.response)
          if (error.response.status === 401) {
            store.removeItem('token')
            store.removeItem('accessToken')
            handleLogout && handleLogout()
          }
        return Promise.reject(error.response)
      }
    )
  },
  blob: blob,
  isLoggedIn: () => store.getItem('refresh'),
  loggedInUser: () => store.getItem('user') || 'unknown',
  logout: () => {
    store.removeItem('token')
    store.removeItem('refresh')
    store.removeItem('accessToken')
    store.removeItem('refreshToken')
  },
  authenticate: ({ username, password }, setScopes, setupSettings) => {
    const config = {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }

    const params = new URLSearchParams()
    params.append('username', username)
    params.append('password', password)

    return post('token', params, config)
      .then((result) => {
        store.setItem('token', result.data.access_token)
        store.setItem('refresh', result.data.refresh_token)
        store.setItem('refreshToken', result.data.refresh_token)
        store.setItem('accessToken', result.data.access_token)
        store.setItem('user', username)
        store.setItem('carrier_type', result.data.carrier_type)
        return post('user/authorization', params, config).then((resp) => {
          if (resp.data.market_names) {
            const currMar = resp.data?.settings?.currentMarket ? JSON.parse(resp.data.settings.currentMarket) : ''
            if (!currMar) {
              setCurrentMarkets([resp.data.market_names?.sort()?.[0]].filter(Boolean))
            }
          }
          setScopes && setScopes(resp.data)
          if (setupSettings) {
            setupSettings(resp.data.settings)
          }
          return result.data.access_token
        })
      })
      .catch((error) => {
        let title = 'Unknown Auth Failure'
        let content = 'Check browser logs for more'
        switch (error?.status) {
          case undefined:
            title = 'Network or CORS error'
            content = 'Please check browser console log'
            break
          case 401:
            title = 'Invalid User Credentials'
            content = 'Please validate your login.'
            break
          case 502:
            title = 'Backend Server Error 502'
            content = 'Is the backend running?'
            break
        }

        Modal.error({
          okButtonProps: {
            style: {
              backgroundColor: 'var(--nav-color)',
            },
          },
          title,
          content,
        })
      })
  },

  sso_authenticate: (token, setScopes, setupSettings) => {
    return post('sso/token', { token }, {})
      .then((result) => {
        store.setItem('token', result.data.access_token)
        store.setItem('refresh', result.data.refresh_token)
        store.setItem('refreshToken', result.data.refresh_token)
        store.setItem('accessToken', result.data.access_token)
        store.setItem('user', result.data.username)
        store.setItem('carrier_type', result.data.carrier_type)
        return post('user/authorization').then((resp) => {
          setScopes && setScopes(resp.data)
          if (setupSettings) {
            setupSettings(resp.data.settings)
          }
          return result.data.access_token
        })
      })
      .catch((error) => {
        if (error === undefined) {
          Modal.error({
            title: 'Network or CORS error',
            content: 'Please check browser console log',
          })
          return
        }
        if (error.status === 502) {
          Modal.error({
            title: 'Backend Server Error 502',
            content: 'Is the backend running?',
          })
          return
        }
        if (error.status === 401) {
          Modal.error({
            title: 'Invalid User Credentials',
            content: 'Please validate your login.',
          })
          return
        }

        Modal.error({
          title: 'Unknown Auth Failure',
          content: 'Check browser logs for more',
        })
      })
  },

  // Metadata Endpoints
  getMetadata: (params) => post('metadata', { params }),

  // Best Buy Endpoints
  getGoogleSheetKey: () => post('best_buy/get_google_sheet_key'),
  getAuthorization: () => post('user/authorization'),
  getUser: () => post('user/info'),

  // Action Block Endpoints
  assignActionBlock: (payload) => post('driver_schedule/action_block/assign', payload),
  removeActionBlock: (payload) => post('driver_schedule/action_block/unassign', payload),
  insertBeforeActionBlock: (payload) => post('driver_schedule/assign', payload), // action: 'insert_before_action'
  insertAssignedBeforeActionBlock: (payload) => post('driver_schedule/move', payload),

  // Order Management Endpoints

  assignToCarrier: (order_id, carrier, carrier_window_start, carrier_window_end) =>
    post('order/assign_to_carrier', {
      order_id,
      carrier,
      carrier_window_start,
      carrier_window_end,
    }),
  sendToCarrier: (order_ids, force) => post('order/send_to_carrier', { order_ids, force }),
  completeOrder: (order_ids) => post('order/mark_complete', { order_ids }),
  downloadOrdersByDrops: (payload) =>
    post('order/export_by_site_product', { ...payload, filter: getFilterWithCalendarShiftDate(payload.filter) }),
  downloadBulkOrders: (filter) => {
    return blob('order/bulk_orders_export', {
      filter: getFilterWithCalendarShiftDate(filter),
      markets: getCurrentMarkets(),
    })
  },
  uploadBulkOrders: (file) => uploadFile('order/bulk_orders_import', file),

  // Carrier Specific (Crossroads) Order Management EP's
  crossroadsCarrierReject: (payload) => post('crossroads/order/reject', payload, null),

  crossroadsOrderChangeSummary: (change_id) =>
    post('crossroads/order_change_queue/change_summary', undefined, {
      change_id,
    }),
  crossroadsOrderChangeAccept: (change_id) => post('crossroads/order_change_queue/accept', undefined, { change_id }),
  crossroadsOrderChangeReject: (change_id) => post('crossroads/order_change_queue/reject', undefined, { change_id }),

  // Pricing Endpoints
  getPriceMetadata: () => post('price_management/metadata'),
  savePrice: (price, as_of) => post('price_management/update', { ...price, as_of }),
  downloadPriceExcel: (req = {}) => blob('price_management/export', req),
  downloadPriceExcelLegacy: (as_of = {}) => blob('price_management/export_legacy_excel', null, null, { as_of }),
  priceManagementOverview: (payload) => post('price_management/overview', payload),
  uploadPriceExcel: (file) =>
    uploadFile('price_management/import', file, {
      headers: { Accept: 'application/json' },
    }),
  // Body must be `{}` (not undefined) so the market injector can attach `markets` here.
  getSupplyOutages: (as_of_utc) => post('price_management/outage/as_of', {}, { as_of_utc }),
  upsertSupplyOutage: (outage, as_of_utc) => post('price_management/outage/upsert', outage, { as_of_utc }),
  cancelSupplyOutage: (outage_id) => post('price_management/outage/cancel', undefined, { outage_id }),

  // Load Number Endpoints
  getLoadNumbers: (filter = {}) => post('load_number/overview', { filter }),
  downloadLNExcel: () => getBlob('load_number/export'),
  uploadLoadNumbers: (file) => uploadFile('load_number/import', file),

  // Load Number Overrides Endpoints
  getLoadNumbersOverrides: (filter = {}) => post('load_number_overrides/overview', { filter }),
  downloadLoadNumberOverrides: () => getBlob('load_number_overrides/export'),
  uploadLoadNumberOverrides: (file) => uploadFile('load_number_overrides/import', file),

  // Destination Code Endpoints
  uploadDestinationCodes: (file) => uploadFile('codes/destination_code/import', file),
  downloadDestinationCodes: () => getBlob('codes/destination_code/export'),

  // Product Code Endpoints
  uploadProductCodes: (file) => uploadFile('codes/product_code/import', file),
  downloadProductCodes: () => getBlob('codes/product_code/export'),

  // Product Endpoints
  uploadProducts: (file) => uploadFile('product/import', file),
  downloadProducts: () => getBlob('product/export'),

  // Blend Code Endpoints
  uploadBlendCodes: (file) => uploadFile('codes/blend_code/import', file),
  downloadBlendCodes: () => getBlob('codes/blend_code/export'),

  // Solver Endpoints
  killRun: (history_id) => post('solver/kill_run', { history_id }),
  downloadResults: (history_id) => getBlob('download_excel_results', { history_id }),
  downloadTempModel: (history_id) => getBlob('download_excel_temp_model', { history_id }),
  getTsdResultsJson: (history_id) => post('tsd_results_json', null, { history_id }),
  getTsdDebugInput: (history_id: string) => post('solver/debug/tsd/input', { history_id }),
  getTsdDebugOutput: (history_id: string) => post('solver/debug/tsd/output', { history_id }),
  runModel: (parameters) => post('solver/run', parameters),
  reRunModel: (history_id) => post('solver/re_run', null, { history_id }),
  getStoreMarkets: () => post('solver/store_markets'),
  getSolverState: (history_id) => post('solver/state', { history_id }),
  getSmartSupplyState: (smart_supply_id) => post('smart_supply/state', { smart_supply_id }),
  cancelSmartSupply: (smart_supply_id) => post('smart_supply/cancel', undefined, { smart_supply_id }),

  // Forecast Endpoints
  getForecast: (filter = {}) => post('forecast/overview', { filter }),

  // Store Endpoints
  getOrderByStore: (id) => post('store_monitor/orders', { id }),
  getStoreById: (id) => post('store/by_id', { id }),
  getStoresByNumber: (store_numbers) => post('store/by_ids', { store_numbers }),
  getStoreView: () => post('store/store_view', {}),
  updateStore: (store) => post('store/update', store),
  uploadLayout: (store_id, file) => uploadFile('store/upload_layout', file, { store_id }),
  removeLayout: (store_id) => post('store/remove_layout', null, { store_id }),
  getStoreForecast: (store_number, tank_ids, filters, cancelToken) =>
    post('store/forecast', { filters, tank_ids }, { store_number }, cancelToken),
  getSiteDetailsForecast: (store_number, tank_id) =>
    post('store/forecast_grid', {
      filters: {
        actual: 21,
        forecast: 10,
      },
      tank_id,
      store_number,
    }),
  deleteStore: (id) => post('store/delete', id),
  downloadSites: () => getBlob('store/export'),
  uploadSites: (file) => uploadFile('store/import', file),
  downloadUnavailableHours: () => getBlob('store/unavailable_hours/export'),
  uploadUnavailableHours: (file) => uploadFile('store/unavailable_hours/import', file),
  downloadNoForecastHours: () => getBlob('store/no_forecast_hours/export'),
  uploadNoForecastHours: (file) => uploadFile('store/no_forecast_hours/import', file),
  downloadCompartmentRestrictions: () => getBlob('compartment_restriction/export'),
  uploadCompartmentRestrictions: (file: FormData) => uploadFile('compartment_restriction/import', file),
  getStoreManifolds: (store_number) => post('store/manifold_tanks', null, { store_number }),
  getTankMonitorConfig: (store_number) => post('store/tank_monitor_information', undefined, { store_number }),
  updateTankMonitor: (req) => post('store/upsert_tank_monitor_config', req),
  updateTank: (tank) => post('store/tank/update', tank),
  exportTanks: () => getBlob('store/tank/export'),
  importTanks: (file) => uploadFile('store/tank/import', file),

  // Tank Transitions
  createTankTransitions: (tankTransition) => post('tank_transition/create', tankTransition),

  // Tank Strategies
  overrideTankStrategies: (request) => post('tank_strategy/override', request),
  setTankStrategySplits: (request) => post('tank_strategy/set_splits', request),
  removeTankStrategies: (request) => post('tank_strategy/remove', request),

  // Tank Supply Defaults
  assignTankSupply: (request) => post('store/tank/supply/assign', request),
  assignTankSupplyOverride: (payload) => post('store/tank/supply/assign_override', payload),
  getTankSupplyDownload: () => getBlob('store/tank/supply/download'),

  // Product Supply Plan Import/Export
  exportProductSupplyPlan: (data) => blob('product_supply_plan/export', data),
  exportMissingSupplyPlan: (data) => blob('product_supply_plan/missing/export', data),
  importProductSupplyPlan: (file) => uploadFile('product_supply_plan/import', file),

  // Product Endpoints
  getProductNames: () => post('product/names'),
  getProductMetadata: () => post('product/metadata'),

  getProducts: (filter = {}) => post('product/overview', { filter }),
  updateBlend: (product_id, blend) => post('product/update_blend', { product_id, ...blend }),
  deleteBlend: (product_id, blend_name) => post('product/delete_blend', null, { product_id, blend_name }),

  // Sector Endpoints
  /**
   * @param {Object} sector - The sector to create
   * @param {string} sector.id - The sector's parent market id (required)
   * @param {string} sector.name - The name of the sector (required)
   * @param {string} sector.trailer_config - The trailer config associated with this sector (required)
   */
  createSector: (sector) => post('market/sector/create', sector),
  /**
   * @param {Object} sector - The sector to create
   * @param {string} sector.id - The id of the sector to udpate (required)
   * @param {string} sector.name - The name of the sector (required)
   * @param {string} sector.trailer_config - The trailer config associated with this sector (required)
   */
  updateSector: (sector) => post('market/sector/update', sector),
  exportSectors: () => getBlob('market/sector/export'),
  importSectors: (file) => uploadFile('market/sector/import', file),
  exportMarkets: () => getBlob('market/export'),

  // Counterparty Endpoints

  getCarriers: () => post('counterparty/carrier_names'),
  getCounterpartyMetadata: () => post('counterparty/metadata'),
  downloadCounterparty: () => getBlob('counterparty/export'),
  uploadCounterparty: (file) => uploadFile('counterparty/import', file),

  // Location Endpoints
  getLocations: (filter = {}) => post('location/overview', { filter }),
  createLocation: (location) => post('location/create', location),
  updateLocation: (location) => post('location/update', location),
  getSupplyZoneMeta: () => post('supply_zone/metadata'),
  createSupplyZone: (supplyZone) => post('supply_zone/create', null, { name: supplyZone.name }),
  deleteSupplyZone: (id) => post('supply_zone/delete', { id }),
  renameSupplyZone: (supply_zone_id, new_name) => post('supply_zone/rename', undefined, { supply_zone_id, new_name }),
  downloadLocations: () => getBlob('location/export'),
  uploadLocations: (file) => uploadFile('location/import', file),

  // Trailer Endpoints
  getTrailers: (filter = {}) => post('trailer/overview', { filter }),
  downloadTrailers: () => getBlob('trailer/export'),
  uploadTrailers: (file) => uploadFile('trailer/import', file),
  getTrailerMetadata: () => post('trailer/metadata'),
  createTrailer: (trailer) => post('trailer/create', trailer),
  updateTrailer: (trailer) => post('trailer/update', trailer),

  // Trailer Configuration
  downloadTrailerConfig: () => getBlob('trailer/trailer_config/export'),
  uploadTrailerConfigs: (file) => uploadFile('trailer/trailer_config/import', file),

  // Tractor Endpoints

  getTractors: (filter = {}) => post('tractor/overview', { filter }),
  getTractorMetadata: () => post('tractor/metadata'),
  createTractor: (tractor) => post('tractor/create', tractor),

  downloadTractors: () => getBlob('tractor/export'),
  uploadTractors: (file) => uploadFile('tractor/import', file),

  // Depot Endpoints
  getDepots: (filter = {}) => post('depot/overview', { filter }),
  createDepot: (depot) => post('depot/create', depot),
  updateDepot: (depot) => post('depot/update', depot),
  downloadDepots: () => getBlob('depot/export'),
  uploadDepots: (file) => uploadFile('depot/import', file),

  // Truck Rate Endpoints
  setTruckRates: (truck_rate_id, rates) => post('truck_rate/set_rates', { truck_rate_id, rates }),
  downloadTruckRates: () => getBlob('truck_rate/export'),
  uploadTruckRates: (file) => uploadFile('truck_rate/import', file),

  // Driver Freight Rate Endpoints
  downloadDriverFreightRates: () => getBlob('payroll/driver_rate/export'),
  uploadDriverFreightRates: (file) => uploadFile('payroll/driver_rate/import', file),

  // Routes
  downloadRoutes: (gridRequest) => postBlob('logistics/route/export', gridRequest),
  uploadRoutes: (file) => uploadFile('logistics/route/import', file),

  // Order Movements
  downloadOrderMovements: (gridRequest) => postBlob('bol/order_movements/export', gridRequest),

  // Freight Invoices
  downloadInvoiceExport: (gridRequest, view) => postBlob(`freight/invoice/export?view=${view}`, gridRequest),

  // Driver Management Endpoints
  getDriverMetadata: () => post('driver/metadata'),
  getDrivers: (filter = {}) => post('driver/overview', filter),
  downloadDrivers: () => getBlob('driver/export'),
  uploadDrivers: (file) => uploadFile('driver/import', file),
  getDriverByID: (driver_id) => post('driver/by_id', { id: driver_id }),
  upsertDriver: (driver) => post('driver/upsert', driver),
  createEvent: (event) => post('driver/create_event', event),
  deleteEvent: (event) => post('driver/delete_event', event),
  updateMaintenanceFlag: (tractor_number) => post('driver/update_maintenance_flag', null, { tractor_number }),
  generateTemplateSchedule: (req) => post('driver/generate_template_schedule', req),
  getDriverPayConfig: (driver_id) => post('driver/pay_configuration', null, { driver_id }),
  saveDriverPayConfig: (driver_id, name, date) => post('driver/set_pay_configuration', null, { driver_id, name, date }),
  autoGenerateSchedules: (driver_ids) => post('driver/auto_generate_schedules', { driver_ids: [driver_ids] }, null),

  // Payroll Endpoints
  approvalAllPayroll: (ids) => post('payroll/approve_all', ids),
  recalculateAllPayroll: (date) => post('payroll/recalculate_all_payroll', null, { date }),
  getPayrollConfigNames: () => post('payroll/payroll_config/names'),
  downloadPayrollConfigs: () => getBlob('payroll/payroll_config/export'),
  uploadPayrollConfigs: (file) => uploadFile('payroll/payroll_config/import', file),
  downloadDeemedHourLanes: () => getBlob('payroll/deemed_hour_lane/export'),
  uploadDeemedHourLanes: (file) => uploadFile('payroll/deemed_hour_lane/import', file),
  getScheduledSpecialPayTypes: () => post('payroll/special_pay/list_scheduled'),
  getSpecialPayroll: (filter = {}, cancelToken) => post('payroll/special_pay/overview', filter, null, cancelToken),
  exportPayroll: (date, download_format) => getBlob('payroll/export', { date, download_format }),
  approveSpecialPay: (special_pay_id) => post('payroll/special_pay_request/approve', null, { special_pay_id }),
  rejectSpecialPay: (special_pay_id, reason) =>
    post('payroll/special_pay_request/reject', null, {
      special_pay_id,
      reason,
    }),
  downloadSpecialPayConfig: () => getBlob('payroll/special_pay/export'),
  uploadSpecialPayConfig: (file) => uploadFile('payroll/special_pay/import', file),
  downloadSpecialPayOverrides: () => getBlob('payroll/special_pay/override/export'),
  uploadSpecialPayOverrides: (file) => uploadFile('payroll/special_pay/override/import', file),

  importPayLaneRates: (file) => uploadFile('payroll/paylane/import', file),
  exportPayLaneRates: () => getBlob('payroll/paylane/export'),

  // special pay overrides
  getDriverNames: () => post('driver/driver_view'),
  getSpecialPayItems: () => post('payroll/special_pay/names'),
  getSpecialPayOverrides: () => post('payroll/special_pay/override/overview'),
  upsertSpecialPayOverride: (override) => post('payroll/special_pay/override/upsert', override),
  deactivateSpecialPayOverride: (overrideId) =>
    post('payroll/special_pay/override/deactivate', null, { id: overrideId }),

  // freight surcharge
  freightSurchargeMetada: () => post('freight_surcharge/metadata'),
  createFreightSurcharge: (surcharge) => post('freight_surcharge/create', surcharge),
  upsertFreightSurcharge: ({ id, effective_from, surcharge }) =>
    post('freight_surcharge/update', { id, effective_from, surcharge }),
  getFreightSurcharges: ({ effective_from, effective_to }) =>
    post('freight_surcharge/overview', { effective_from, effective_to }),
  deleteFreightSurcharge: (surchargeId) => post('freight_surcharge/delete', { id: surchargeId }),
  pointToPointOverview: () => post('freight_point_to_point/overview'),
  pointToPointImport: (file) => uploadFile('freight_point_to_point/import', file),
  pointToPointExport: () => getBlob('freight_point_to_point/export'),

  // Store Monitoring Endpoints
  getTankRundown: (store_number, tank_id, cancelToken) =>
    post('store_monitor/tank_rundown', { store_number, tank_id }, undefined, cancelToken),
  getOrderStatus: (order_id) => post('store_monitor/order_status', { order_id }, null),
  // Product Endpoints
  updateProduct: (product) => post('product/update', product),
  createProduct: (product) => post('product/create', product),

  // Alerts endpoints
  getAlerts: () => post('alert/get'),
  resolveAlert: (alert_id) => post('alert/resolve', null, { alert_id }),
  resolveDriverAlert: (issue_id, driver_schedule_id) =>
    post('driver_schedule/resolve_issue', { issue_id, driver_schedule_id }),

  // Order Movements Endpoints

  getOrderMovementDetails: (order_id) => post('bol/order_movement_details', undefined, { order_id }),

  getBOLImagePDF: (bol_id) => getBlob('bol/download_bol_pdf', { bol_id }),
  uploadBOLImage: (order_id, bol, file) =>
    uploadFile(
      'bol/upload_bol_image',
      file,
      { order_id, number: bol.number, load_or_drop: bol.type, document_type: 'bill of lading' },
      false
    ),
  deleteBOLImage: (bol_id, photo) => post('bol/delete_bol_image', null, { bol_id, photo }),
  uploadDropAttachment: (order_id, site_number, file) =>
    uploadFile('bol/upload_drop_attachments', file, { site_number, order_id }, false),
  deleteDropAttachment: (order_number, store_number, photo) =>
    post('bol/delete_drop_attachments', null, { order_number, store_number, photo }),
  getDropAttachmentsPDF: (order_number) => getBlob('bol/download_drop_attachments', { order_number }),
  getDeliveryTicketPDF: (order_number, store_number) =>
    blob('bol/download_delivery_ticket', { order_number, store_number }),
  sendDeliveryTicket: (order_number, store_number) => blob('bol/send_delivery_ticket', { order_number, store_number }),

  // Auto Dispatch
  automatedDispatchStart: (payload) => post('auto_dispatch/start', payload, {}),

  // Best Supply By Tank
  downloadBestSupplyByTank: (date) =>
    getBlob('best_supply_record/export', {
      date: moment(date).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm:ss'),
    }),

  // Dispatch Directive
  getSupplyReportAll: (filter = {}, cancelToken) =>
    post(
      'dispatch_directive/supply_overview',
      {
        filter,
        timezone: getTimezone(),
      },
      null,
      cancelToken
    ),
  getDailyDirectivePrices: (filter, as_of, curve_group_id) =>
    post('directive_management/prices', { filter, as_of, curve_group_id }),
  getDailyDirectiveAnalytics: (request) => post('directive_management/daily_analytics', { ...request }),
  directiveById: (group_id, as_of) => post('dispatch_directive/group_by_id', { group_id, as_of }),
  getDispatchDirectoryHistory: (curve_group_id, as_of) =>
    post('dispatch_directive/history', undefined, { curve_group_id, as_of }),
  getMonthlyAnalytics: (curve_group_id, as_of, monthly_allocation) =>
    post('directive_management/selected_monthly_analytics', {
      curve_group_id,
      as_of,
      monthly_allocation,
    }),
  updateAllocations: (request) => post('directive_management/update_allocations', { ...request }),
  updateConstraints: (request) => post('directive_management/update_constraints', { ...request }),
  uploadDirectives: (file) => uploadFile('directive_management/import', file, undefined, false),
  downloadDirectives: (as_of, req = {}) => getBlob('directive_management/export', { ...req, as_of }, false),
  uploadHistoricalVolume: (file) => uploadFile('directive_management/external_bols/import', file),
  downloadHistoricalTemplate: () => getBlob('directive_management/external_bols/export'),

  // Analytics EP's
  getDriverAnalytics: (dates) =>
    post('driver_analytics/driver_analytics', {
      dates,
      limit: 7000,
    }),
  getAnalyticMetadata: () => post('driver_analytics/metadata'),

  updateOrderFlag: (payload) => post('order/flag/update', payload),
  getFlagReasonCodes: () => post('order/reason_code/list'),
  downloadInventoryCosting: (payload) => getBlob('valuation/valuation/export', payload),
  checkExportStatus: (order_id) => post('order/is_exported', {}, order_id),
  getUserSettingByKey: (key) => post('user/settings/get', {}, { key }),
  updateUserSetting: (key, value) => uploadFile('user/settings/set', value, { key }, false),
  resetUserSetting: (key) => post('user/settings/delete'),
  checkDuplicateBolNumber: (payload) => post('store_monitor/site_view/is_duplicate_bol_number', payload),
  getOrderById: (order_id) => post('order/by_id', { order_id }),

  // Driver Tracking Endpoints
  getAllDriverTracking: (filters: components['schemas']['TrackingReq']) => post('driver_tracking/all', filters),
  getPointsOfInterest: () => post('driver_tracking/points_of_interest', {}),
}

export default q
